import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { ordersAPI, leadsAPI, messagesAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft,
  Upload,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Loader2,
  FileSpreadsheet,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SellerOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [leads, setLeads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [orderRes, leadsRes, messagesRes] = await Promise.all([
        ordersAPI.getById(id),
        leadsAPI.getByOrder(id),
        messagesAPI.getByOrder(id),
      ]);
      setOrder(orderRes.data);
      setLeads(leadsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/seller/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-sky-100 text-sky-800',
      in_fulfillment: 'bg-blue-100 text-blue-800',
      delivered: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-green-100 text-green-800',
      disputed: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await ordersAPI.updateStatus(id, newStatus);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const response = await messagesAPI.send({
        order_id: id,
        text: newMessage,
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const parseCSV = (csv) => {
    const lines = csv.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const leads = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const lead = {};
      headers.forEach((header, index) => {
        lead[header] = values[index] || '';
      });
      leads.push(lead);
    }
    
    return leads;
  };

  const handleUploadLeads = async () => {
    if (!csvContent.trim()) {
      toast.error('Please paste CSV content');
      return;
    }
    
    const parsedLeads = parseCSV(csvContent);
    if (parsedLeads.length === 0) {
      toast.error('No valid leads found in CSV');
      return;
    }
    
    setUploading(true);
    try {
      const response = await leadsAPI.upload({
        order_id: id,
        leads: parsedLeads.map(l => ({
          first_name: l.first_name || '',
          last_name: l.last_name || '',
          email: l.email || '',
          phone: l.phone || '',
          company_name: l.company_name || l.company || '',
          job_title: l.job_title || l.title || '',
          linkedin_url: l.linkedin_url || l.linkedin || '',
          source: l.source || 'CSV Upload',
          consent_type: l.consent_type || 'explicit',
        }))
      });
      
      setUploadResult(response.data);
      toast.success(`Uploaded ${response.data.valid} valid leads`);
      fetchData();
    } catch (error) {
      toast.error('Failed to upload leads');
    } finally {
      setUploading(false);
    }
  };

  if (loading || !order) {
    return (
      <Layout showFooter={false}>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4" />
            <div className="h-64 bg-slate-200 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  const validLeads = leads.filter(l => l.validation_status === 'valid');
  const invalidLeads = leads.filter(l => l.validation_status !== 'valid');
  const deliveryProgress = (order.leads_delivered / order.quantity) * 100;

  return (
    <Layout showFooter={false}>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="seller-order-detail">
        <Button variant="ghost" className="mb-6" onClick={() => navigate('/seller/dashboard')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-slate-900">{order.campaign_name}</h1>
              <Badge className={getStatusColor(order.status)}>
                {order.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-slate-600">Buyer: {order.buyer_name}</p>
          </div>
          <div className="flex gap-3">
            <Select value={order.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[180px]" data-testid="status-select">
                <SelectValue placeholder="Update Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="in_fulfillment">In Fulfillment</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button data-testid="upload-leads-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Leads
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Upload Leads</DialogTitle>
                  <DialogDescription>
                    Paste your CSV content with headers: first_name, last_name, email, phone, company_name, job_title
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <Textarea
                    placeholder="Paste CSV content here..."
                    value={csvContent}
                    onChange={(e) => setCsvContent(e.target.value)}
                    rows={10}
                    className="font-mono text-sm"
                    data-testid="csv-input"
                  />
                  
                  {uploadResult && (
                    <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                      <div className="flex items-center gap-2 text-emerald-600">
                        <CheckCircle className="w-4 h-4" />
                        <span>{uploadResult.valid} valid leads uploaded</span>
                      </div>
                      {uploadResult.invalid > 0 && (
                        <div className="flex items-center gap-2 text-red-600">
                          <XCircle className="w-4 h-4" />
                          <span>{uploadResult.invalid} invalid leads</span>
                        </div>
                      )}
                      {uploadResult.duplicate > 0 && (
                        <div className="flex items-center gap-2 text-amber-600">
                          <AlertCircle className="w-4 h-4" />
                          <span>{uploadResult.duplicate} duplicates skipped</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button onClick={handleUploadLeads} disabled={uploading} className="w-full">
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Upload & Validate
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Delivery Progress */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Delivery Progress</span>
              <span className="text-sm text-slate-600">
                {order.leads_delivered} / {order.quantity} leads
              </span>
            </div>
            <Progress value={deliveryProgress} className="h-3" />
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="leads">
              <TabsList>
                <TabsTrigger value="leads">Uploaded Leads ({leads.length})</TabsTrigger>
                <TabsTrigger value="messages">Messages ({messages.length})</TabsTrigger>
                <TabsTrigger value="targeting">Targeting</TabsTrigger>
              </TabsList>

              <TabsContent value="leads" className="mt-4">
                <Card>
                  <CardContent className="p-0">
                    {leads.length === 0 ? (
                      <div className="text-center py-12">
                        <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-medium text-slate-700 mb-2">No leads uploaded yet</h3>
                        <p className="text-sm text-slate-500 mb-4">Upload leads using the button above</p>
                        <Button size="sm" onClick={() => setUploadOpen(true)}>
                          Upload Leads
                        </Button>
                      </div>
                    ) : (
                      <ScrollArea className="h-[400px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Company</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {leads.map((lead) => (
                              <TableRow key={lead.id}>
                                <TableCell className="font-medium">
                                  {lead.first_name} {lead.last_name}
                                </TableCell>
                                <TableCell>{lead.email}</TableCell>
                                <TableCell>{lead.company_name}</TableCell>
                                <TableCell>
                                  {lead.validation_status === 'valid' ? (
                                    <Badge variant="outline" className="text-emerald-600">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Valid
                                    </Badge>
                                  ) : lead.is_duplicate ? (
                                    <Badge variant="outline" className="text-amber-600">
                                      Duplicate
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-red-600">
                                      <XCircle className="w-3 h-3 mr-1" />
                                      Invalid
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    <ScrollArea className="h-[300px] mb-4 pr-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          No messages yet
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`message-bubble ${msg.sender_id === user.id ? 'sent' : 'received'}`}
                              >
                                <div className="text-xs opacity-70 mb-1">{msg.sender_name}</div>
                                <div>{msg.text}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                      />
                      <Button onClick={handleSendMessage} disabled={sendingMessage}>
                        {sendingMessage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="targeting" className="mt-4">
                <Card>
                  <CardContent className="pt-6 space-y-4">
                    <div>
                      <div className="text-sm text-slate-500 mb-2">Regions</div>
                      <div className="flex flex-wrap gap-2">
                        {order.targeting?.regions?.map((r, i) => (
                          <Badge key={i} variant="outline">{r}</Badge>
                        ))}
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <div className="text-sm text-slate-500 mb-2">Industries</div>
                      <div className="flex flex-wrap gap-2">
                        {order.targeting?.industries?.map((ind, i) => (
                          <Badge key={i} variant="secondary">{ind}</Badge>
                        ))}
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <div className="text-sm text-slate-500 mb-2">Job Titles</div>
                      <div className="flex flex-wrap gap-2">
                        {order.targeting?.job_titles?.map((t, i) => (
                          <Badge key={i} variant="outline">{t}</Badge>
                        ))}
                      </div>
                    </div>
                    {order.campaign_objective && (
                      <>
                        <Separator />
                        <div>
                          <div className="text-sm text-slate-500 mb-2">Campaign Objective</div>
                          <p className="text-slate-700">{order.campaign_objective}</p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Buyer</span>
                  <span className="font-medium">{order.buyer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Package</span>
                  <span className="font-medium">{order.selected_tier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total Leads</span>
                  <span className="font-medium">{order.quantity}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-medium">Revenue</span>
                  <span className="font-bold">${order.total_price.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Upload Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Valid Leads</span>
                  <span className="text-emerald-600 font-medium">{validLeads.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Invalid/Duplicate</span>
                  <span className="text-red-600 font-medium">{invalidLeads.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Remaining</span>
                  <span className="font-medium">{Math.max(0, order.quantity - order.leads_delivered)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SellerOrderDetail;
